import { useCallback, useEffect, useState } from 'react';
import { avisarErro } from '../componentes/Avisos.tsx';
import { Icone } from '../componentes/Icone.tsx';
import { Valor } from '../componentes/Valor.tsx';
import {
  ErroDaApi,
  apiFinanceiro,
  type CobrancaNoPainel,
  type CustoDasFontes,
  type GastoDaFonte,
  type PainelFinanceiro,
} from '../lib/api.ts';
import { paraTela } from '../lib/datas.ts';
import { iniciais } from '../lib/iniciais.ts';
import '../estilos/sistema.css';
import '../estilos/financeiro.css';

/**
 * A aba Financeiro.
 *
 * Responde as quatro perguntas que o Richard pediu em 15/08/2026: o que está
 * vencido, o que vence em breve, quanto entrou no mês, e quanto foi contratado
 * contra quanto falta receber.
 *
 * **Ela lê e mostra; não cria nada.** Contrato e parcela já guardam tudo, e o
 * contrato é o dono único do dinheiro. Para dar baixa numa cobrança, o caminho
 * é a ficha do cliente — onde está o contrato inteiro, com contexto. Um botão
 * de baixa aqui pareceria prático e faria a pessoa confirmar pagamento olhando
 * uma linha solta, sem ver o resto do contrato.
 *
 * **Nada de cobrar o cliente.** Régua de inadimplência e emissão são do Asaas.
 * Aqui se enxerga; quem fala com o cliente é o banco.
 */

/** Quanto tempo de atraso já é grave. Abaixo disso, é atraso de banco. */
const ATRASO_GRAVE = 15;

export function Financeiro({ aoAbrirCliente, aoExpirarSessao }: {
  aoAbrirCliente: (id: string) => void;
  aoExpirarSessao: () => void;
}) {
  const [painel, setPainel] = useState<PainelFinanceiro | null>(null);
  const [fontes, setFontes] = useState<CustoDasFontes | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setErro(null);
      /*
        ⚠️ **Duas chamadas, e o gasto NÃO derruba a página se falhar.** O painel
        de cobranças é o que a aba existe para mostrar; o gasto com fontes é
        acréscimo. Numa chamada só, uma consulta pesada ao histórico deixaria o
        Richard sem ver o que está vencido.
      */
      setPainel(await apiFinanceiro.painel());
      setFontes(await apiFinanceiro.fontes().catch(() => null));
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      avisarErro(e);
      setErro(e instanceof Error ? e.message : 'Não consegui carregar o financeiro.');
    }
  }, [aoExpirarSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (!painel) {
    return <div className="carregando">{erro ?? 'Carregando o financeiro…'}</div>;
  }

  const { resumo } = painel;
  const temVencido = painel.vencidas.length > 0;

  return (
    /*
      ⭐ **Sistema visual 2026, parte 4 — o Financeiro, 14/09/2026.** A folha de
      identidade, os quatro números como indicadores e cada lista numa folha.
      ⛔ **Mudou a aparência, não o que se lê:** todo valor continua passando
      pelo `Valor` (o olho do sigilo esconde todos), e a linha continua levando
      à ficha, que é onde se dá baixa.
    */
    <div className="financeiro-2026">
      <section className="s-folha s-identidade financeiro-identidade">
        <div className="s-monograma financeiro-monograma" aria-hidden="true">
          <Icone nome="financeiro" tamanho={28} />
        </div>
        <div className="financeiro-identidade-texto">
          <span className="s-sobrelinha">Gestão</span>
          <h1 className="titulo">Financeiro</h1>
          <p className="financeiro-apoio">O que foi contratado, o que entrou e o que está para vencer.</p>
        </div>
      </section>

      {/* Os quatro números. O vencido vem primeiro e em vermelho: é o único
          que pede ação hoje — os outros são retrato, não tarefa. */}
      <div className="s-indicadores financeiro-numeros">
        <div className={'s-indicador financeiro-numero' + (temVencido ? ' financeiro-alerta' : '')}>
          <span className="s-micro">
            <Icone nome="atencao" tamanho={14} />
            Vencido e não pago
          </span>
          <Valor de={resumo.vencido} className="s-indicador-valor" />
          <span className="s-indicador-nota">
            {painel.vencidas.length === 0
              ? 'Nada em atraso.'
              : `${painel.vencidas.length} cobrança${painel.vencidas.length > 1 ? 's' : ''} em atraso.`}
          </span>
        </div>

        <div className="s-indicador financeiro-numero">
          <span className="s-micro">
            <Icone nome="relogio" tamanho={14} />
            Vence em {painel.janela} dias
          </span>
          <Valor de={resumo.aVencerNaJanela} className="s-indicador-valor" />
          <span className="s-indicador-nota">
            {painel.aVencer.length === 0
              ? 'Nada previsto no período.'
              : `${painel.aVencer.length} cobrança${painel.aVencer.length > 1 ? 's' : ''} a vencer.`}
          </span>
        </div>

        <div className="s-indicador financeiro-numero">
          <span className="s-micro">
            <Icone nome="concluido" tamanho={14} />
            Entrou em {mesPorExtenso(painel.mes)}
          </span>
          <Valor de={resumo.recebidoNoMes} className="s-indicador-valor" />
          <span className="s-indicador-nota">Baixado com data de pagamento no mês.</span>
        </div>

        <div className="s-indicador financeiro-numero">
          <span className="s-micro">
            <Icone nome="cobranca" tamanho={14} />
            A receber
          </span>
          <Valor de={resumo.aReceber} className="s-indicador-valor" />
          <span className="s-indicador-nota">
            De <Valor de={resumo.contratado} /> contratados · já entraram{' '}
            <Valor de={resumo.recebido} />.
          </span>
        </div>
      </div>

      {erro && <p className="erro">{erro}</p>}

      <Lista
        titulo="Vencidas"
        subtitulo="Passaram da data e não têm baixa. Quem avisa o cliente é o Asaas."
        vazio="Nenhuma cobrança em atraso."
        linhas={painel.vencidas}
        atrasada
        aoAbrirCliente={aoAbrirCliente}
      />

      <Lista
        titulo={`A vencer nos próximos ${painel.janela} dias`}
        subtitulo="O que entra se tudo for pago em dia."
        vazio="Nada a vencer no período."
        linhas={painel.aVencer}
        aoAbrirCliente={aoAbrirCliente}
      />

      {fontes && <GastoComFontes custo={fontes} />}
    </div>
  );
}

function Lista({
  titulo,
  subtitulo,
  vazio,
  linhas,
  atrasada = false,
  aoAbrirCliente,
}: {
  titulo: string;
  subtitulo: string;
  vazio: string;
  linhas: CobrancaNoPainel[];
  atrasada?: boolean;
  aoAbrirCliente: (id: string) => void;
}) {
  return (
    <section
      className={'s-folha financeiro-lista' + (atrasada && linhas.length > 0 ? ' financeiro-lista-atrasada' : '')}
      aria-label={titulo}
    >
      <div className="s-folha-topo">
        <h2 className="s-folha-titulo financeiro-lista-titulo">
          {titulo}
          <span className={'s-pilula' + (atrasada && linhas.length > 0 ? ' s-pilula-vinho' : ' s-pilula-quieta')}>
            {linhas.length}
          </span>
        </h2>
        <p className="subtitulo">{subtitulo}</p>
      </div>

      {linhas.length === 0 ? (
        <p className="vazio">{vazio}</p>
      ) : (
        <ul className="lista-cobrancas">
          {linhas.map((l) => (
            <li key={l.parcelaId}>
              {/* A linha inteira leva à ficha do cliente: é lá que se dá baixa,
                  com o contrato inteiro à vista. */}
              <button
                type="button"
                className="cobranca-linha"
                onClick={() => aoAbrirCliente(l.clienteId)}
              >
                <span className="s-avatar" aria-hidden="true">
                  {iniciais(l.clienteNome)}
                </span>
                <span className="cobranca-quem">
                  <span className="cobranca-cliente">{l.clienteNome}</span>
                  <span className="ajuda">
                    <span className="dado">{l.contratoCodigo}</span> ·{' '}
                    {l.numero === 0 ? 'entrada' : `parcela ${l.numero}`} · {l.contratoNivel}
                  </span>
                </span>

                <span className="cobranca-quando">
                  <span className={'dado' + (atrasada ? ' vencido' : '')}>
                    {paraTela(l.vencimentoEm)}
                  </span>
                  <span
                    className={
                      'ajuda' + (atrasada && l.dias >= ATRASO_GRAVE ? ' ajuda-grave' : '')
                    }
                  >
                    {atrasada
                      ? l.dias === 1
                        ? 'há 1 dia'
                        : `há ${l.dias} dias`
                      : l.dias === 0
                        ? 'hoje'
                        : l.dias === 1
                          ? 'amanhã'
                          : `em ${l.dias} dias`}
                  </span>
                </span>

                <Valor de={l.valor} className="cobranca-valor" />
                <span className="cobranca-seta" aria-hidden="true">
                  <Icone nome="avancar" tamanho={16} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * O GASTO COM AS FONTES PAGAS — pedido do Richard em 24/08/2026.
 *
 * > *"O gasto acumulado ficará no financeiro."*
 *
 * ⚠️ **É o QUARTO dinheiro do Hub, e fica visualmente separado do resto da
 * aba de propósito.** Acima estão contrato e parcela — o que a KAIRÓS
 * **recebe**. Aqui é o que ela **gasta para apurar**. Misturados no mesmo
 * painel de números, alguém somaria os dois um dia.
 *
 * ⚠️ **Isto NÃO é o Fundo de Custas.** Consulta é ferramenta de trabalho da
 * KAIRÓS, como o servidor; o Fundo é desembolso em nome do cliente. As duas
 * coisas parecem iguais numa tela e não são.
 *
 * ⭐ **O número que interessa é o que SOBROU**, não o que se gastou: o piso é
 * pago de todo jeito e a sobra não volta.
 */
function GastoComFontes({ custo }: { custo: CustoDasFontes }) {
  return (
    <>
    {/* ⚠️ O fio com nome separa as duas metades da aba: acima, o que a KAIRÓS
        recebe; abaixo, o que ela gasta. Juntos, alguém somaria os dois. */}
    <p className="s-sobrelinha financeiro-divisa">O que a KAIRÓS gasta</p>
    <section className="s-folha financeiro-fontes" aria-label="Gasto com fontes de pesquisa">
      <div className="s-folha-topo">
      <h2 className="s-folha-titulo">Gasto com fontes de pesquisa</h2>
      <p className="subtitulo">
        O que a KAIRÓS paga para apurar.{' '}
        {custo.periodo === 'ciclo'
          ? 'Apurado no ciclo do plano.'
          : 'Apurado no mês do calendário — o ciclo dos planos ainda não foi informado.'}{' '}
        <strong>Não é receita nem Fundo de Custas</strong> — é custo de operação.
      </p>
      </div>

      <ul className="fontes-pagas">
        {custo.fontes.map((f) => (
          <LinhaDaFonte key={f.chave} fonte={f} />
        ))}
      </ul>

      <p className="ajuda ajuda-solta financeiro-fontes-total">
        No período: <Valor de={custo.gastoNoPeriodo} /> · desde o começo:{' '}
        <Valor de={custo.gastoDesdeSempre} />.
      </p>
    </section>
    </>
  );
}

function LinhaDaFonte({ fonte: f }: { fonte: GastoDaFonte }) {
  /* ⚠️ Nada aqui vira porcentagem quando a sobra é nula: barra sem denominador
     conhecido desenharia um progresso inventado. */
  const usado =
    f.sobra === null ? null : Math.min(100, (Number(f.gasto) / Number(f.piso)) * 100);

  return (
    <li className="fonte-paga">
      <div className="fonte-paga-topo">
        <span className="fonte-paga-nome">{f.nome}</span>
        {f.sobra === null ? (
          <span className="ajuda">
            gastos <Valor de={f.gasto} /> de <Valor de={f.piso} /> por mês
          </span>
        ) : (
          <span className="fonte-paga-sobra">
            ainda cabem <Valor de={f.sobra} className="fonte-paga-sobra-valor" />
          </span>
        )}
      </div>

      {/* A barra só existe quando o período é conhecido. */}
      {usado !== null && (
        <div className="fonte-paga-barra" aria-hidden="true">
          <span style={{ width: `${usado}%` }} />
        </div>
      )}

      <p className="ajuda">
        {f.consultas === 0
          ? 'Nenhuma consulta no período.'
          : `${f.consultas} consulta${f.consultas > 1 ? 's' : ''}`}
        {f.reconsultas > 0 && ` · ${f.reconsultas} sem custo por já estarem na base`}
        {f.teto && (
          <>
            {' '}
            · teto do token <Valor de={f.teto} />
          </>
        )}
      </p>

      {/*
        ⚠️ **A lacuna vem antes dos alertas, e diz ONDE preencher.** É a mesma
        forma do índice de cobertura sem o valor da dívida: o Hub não inventa
        denominador — avisa o que falta.
      */}
      {f.lacuna && <p className="ajuda-alerta ajuda-solta">{f.lacuna}</p>}

      {/*
        ⚠️ **"Pode ter cobrado" é o único aviso que pede ação**, e por isso é o
        único em tom de alerta. Ele manda conferir o extrato da fonte — o Hub
        não tem como saber sozinho.
      */}
      {f.semCustoConhecido > 0 && (
        <p className="ajuda-alerta ajuda-solta">
          {f.semCustoConhecido === 1
            ? '1 consulta pode ter sido cobrada sem o Hub saber'
            : `${f.semCustoConhecido} consultas podem ter sido cobradas sem o Hub saber`}{' '}
          — confira no extrato da fonte.
        </p>
      )}

      {/*
        ⚠️ **Falha cobrada precisa aparecer junto da falha.** Sem isso, "18
        falhas" ao lado de um gasto baixo sugere que falha não custa — e na
        Infosimples paga-se pela resposta: "nada consta" e parâmetro inválido
        cobram.
      */}
      {f.falhas > 0 && (
        <p className="ajuda">
          {f.falhas} falha{f.falhas > 1 ? 's' : ''}
          {f.falhasCobradas > 0
            ? `, das quais ${f.falhasCobradas} foram cobradas assim mesmo`
            : ' — nenhuma cobrada'}
          .
        </p>
      )}
    </li>
  );
}

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

/** `2026-08` → `agosto`. Mês por extenso é prosa, não dado: não vai em mono. */
function mesPorExtenso(mes: string): string {
  return MESES[Number(mes.slice(5, 7)) - 1] ?? mes;
}
